import React, { useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Search, RefreshCw, TrendingUp } from 'lucide-react';

/**
 * TopSearchesPanel
 * Admin-only view of the most-searched terms in the global palette.
 * A LOW click-through rate on a popular query = a page people can't find →
 * a signal to add better labels/keywords in the search index or surface that
 * page more prominently.
 */
const RANGES = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

const TopSearchesPanel = () => {
  const { API } = useContext(AuthContext);
  const [days, setDays] = useState(30);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async (rangeDays) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/search-analytics/top`, {
        params: { days: rangeDays, limit: 25 }
      });
      setRows(res.data?.queries || []);
    } catch (e) {
      toast.error('Failed to load search analytics');
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchRows(days);
  }, [days, fetchRows]);

  return (
    <Card data-testid="top-searches-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2 text-[#2B6B7F]">
              <TrendingUp className="w-5 h-5" />
              Top Searches
            </CardTitle>
            <CardDescription>
              What members are typing into the search bar. Low click-through means people can&apos;t find the page — good signal to add keywords or a new nav item.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            {RANGES.map((r) => (
              <Button
                key={r.days}
                size="sm"
                variant={days === r.days ? 'default' : 'outline'}
                onClick={() => setDays(r.days)}
                className={days === r.days ? 'bg-[#2B6B7F] hover:bg-[#245968]' : ''}
                data-testid={`top-searches-range-${r.days}`}
              >
                {r.label}
              </Button>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchRows(days)}
              data-testid="top-searches-refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="text-center py-6" data-testid="top-searches-empty">
            <Search className="w-10 h-10 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500 italic">
              No searches recorded in the last {days} days yet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="top-searches-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                  <th className="py-2 pr-3">Query</th>
                  <th className="py-2 pr-3 text-right">Searches</th>
                  <th className="py-2 pr-3 text-right">Click-through</th>
                  <th className="py-2 pr-3">Top result clicked</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.query} className="border-b border-slate-100 last:border-0" data-testid={`top-search-row-${r.query}`}>
                    <td className="py-2 pr-3 font-medium text-slate-800">{r.query}</td>
                    <td className="py-2 pr-3 text-right">{r.count}</td>
                    <td className="py-2 pr-3 text-right">
                      <Badge
                        className={
                          r.click_through_rate >= 60
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : r.click_through_rate >= 25
                              ? 'bg-amber-100 text-amber-800 border-amber-200'
                              : 'bg-red-100 text-red-800 border-red-200'
                        }
                      >
                        {r.click_through_rate}%
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-slate-600 truncate max-w-[220px]">
                      {r.top_click || <span className="italic text-slate-400">(no clicks)</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopSearchesPanel;
